export type InboxListSort = 'newest' | 'oldest';
export type InboxListOrder = InboxListSort | 'api-relevance';

export type InboxListOrderOptions = {
    readonly order: InboxListOrder;
};

export type InboxListOrderable = {
    readonly id: string;
    readonly last_active_at: string;
    readonly unread_count: number;
    readonly pending_staged_message_count: number;
};

// Counts a conversation had when the coordinator opened it.
export type InboxStickySnapshot = {
    readonly unread_count: number;
    readonly pending_staged_message_count: number;
};

// Rows read during the current visit, keyed by conversation id. Filtering and
// banding read the snapshot, so marking a row read cannot drop it out of the
// Unread / Needs review pill or sink it to the bottom while the coordinator is
// still clicking through. Rendering uses the live row, so the unread dot
// clears immediately. The set is held for the whole visit and released only
// when the coordinator leaves Conversations; a pill toggle keeps it, because
// the row vanishing mid-triage is the thing being prevented.
export type InboxStickyRows = ReadonlyMap<string, InboxStickySnapshot>;

export type InboxViewOptions = InboxListOrderOptions & {
    readonly unreadOnly: boolean;
    readonly needsReviewOnly: boolean;
    readonly openId: string | null;
    readonly pinnedIndex: number | null;
    readonly filterScopeKey: string;
    readonly stickyRows?: InboxStickyRows;
};

export type InboxOpenedRowSnapshot<T> = {
    readonly filterScopeKey: string;
    readonly row: T;
};

export type InboxFilterScope = {
    readonly organizationId: string | null;
    readonly search: string;
    readonly officeIds: ReadonlyArray<string>;
    readonly assigneeIds: ReadonlyArray<string>;
    readonly includedCaregiverTags: ReadonlyArray<string>;
    readonly excludedCaregiverTags: ReadonlyArray<string>;
    readonly deliveryStates: ReadonlyArray<string>;
    readonly recentOnly: boolean;
    readonly unreadOnly: boolean;
    readonly needsReviewOnly: boolean;
};

export function inboxFilterScopeKey(scope: InboxFilterScope): string {
    return JSON.stringify([
        scope.organizationId,
        scope.search,
        scope.officeIds,
        scope.assigneeIds,
        scope.includedCaregiverTags,
        scope.excludedCaregiverTags,
        scope.deliveryStates,
        scope.recentOnly,
        scope.unreadOnly,
        scope.needsReviewOnly,
    ]);
}

export function isConversationUnread(unreadCount: number): boolean {
    return unreadCount > 0;
}

export function conversationPassesInboxPills(
    conversation: Pick<
        InboxListOrderable,
        'unread_count' | 'pending_staged_message_count'
    >,
    unreadOnly: boolean,
    needsReviewOnly: boolean,
    sticky?: InboxStickySnapshot,
): boolean {
    const counts = sticky ?? conversation;
    if (unreadOnly && !isConversationUnread(counts.unread_count)) {
        return false;
    }
    if (needsReviewOnly && counts.pending_staged_message_count <= 0) {
        return false;
    }
    return true;
}

export function sortInboxConversations<T extends InboxListOrderable>(
    conversations: ReadonlyArray<T>,
    options: InboxListOrderOptions,
): Array<T> {
    if (options.order === 'api-relevance') {
        return [...conversations];
    }
    return [...conversations].sort((left, right) => {
        const leftActiveMs = new Date(left.last_active_at).getTime();
        const rightActiveMs = new Date(right.last_active_at).getTime();
        if (options.order === 'oldest') {
            return leftActiveMs - rightActiveMs;
        }
        return rightActiveMs - leftActiveMs;
    });
}

// Keep the open conversation at the index it had when the coordinator
// opened it (or last changed view options). Other rows may still animate.
// `pinnedRow` is the unfiltered conversation: Unread / Needs review can
// drop it from `sorted` after mark-as-read or a cleared pending reply.
export function pinOpenConversation<T extends { readonly id: string }>(
    sorted: ReadonlyArray<T>,
    openId: string | null,
    pinnedIndex: number | null,
    pinnedRow: T | null = null,
): Array<T> {
    if (openId === null) {
        return [...sorted];
    }
    const open =
        sorted.find((conversation) => conversation.id === openId) ??
        (pinnedRow?.id === openId ? pinnedRow : undefined);
    if (open === undefined) {
        return [...sorted];
    }
    const alreadyVisible = sorted.some(
        (conversation) => conversation.id === openId,
    );
    if (pinnedIndex === null && alreadyVisible) {
        return [...sorted];
    }
    const rest = sorted.filter((conversation) => conversation.id !== openId);
    const index = Math.max(0, Math.min(pinnedIndex ?? 0, rest.length));
    return [...rest.slice(0, index), open, ...rest.slice(index)];
}

// Filter → sort → pin. The open row is re-inserted even when it fails
// Unread / Needs review after mark-as-read or a cleared pending reply.
// Assignee / tag filters stay with the caller so this can run on already-matching rows.
export function visibleInboxRows<T extends InboxListOrderable>(
    conversations: ReadonlyArray<T>,
    options: InboxViewOptions,
    openedRowSnapshot: InboxOpenedRowSnapshot<T> | null = null,
): Array<T> {
    const filtered = conversations.filter((conversation) =>
        conversationPassesInboxPills(
            conversation,
            options.unreadOnly,
            options.needsReviewOnly,
            options.stickyRows?.get(conversation.id),
        ),
    );
    const sorted = sortInboxConversations(filtered, options);
    const pinnedRow =
        options.openId === null
            ? null
            : (conversations.find(
                  (conversation) => conversation.id === options.openId,
              ) ??
              (openedRowSnapshot?.filterScopeKey === options.filterScopeKey &&
              openedRowSnapshot.row.id === options.openId
                  ? openedRowSnapshot.row
                  : null));
    return pinOpenConversation(
        sorted,
        options.openId,
        options.pinnedIndex,
        pinnedRow,
    );
}
